import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Activity as ActivityIcon, BarChart2, MessageCircle, AlertTriangle, Video, Phone, Link as LinkIcon, ExternalLink, Clock, MessageSquare, Info, Lock, Lightbulb, PlayCircle, Star, GraduationCap } from 'lucide-react';
import { leadsAPI } from '../../../api/apiHelper';

export default function Activity() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("Deal Interaction History");
    const [activityData, setActivityData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchActivity = async () => {
            if (!id || id === 'view') {
                if (isMounted) setLoading(false);
                return;
            }
            try {
                if (isMounted) setLoading(true);
                const response = await leadsAPI.getDealActivity(id);
                if (isMounted) {
                    if (response && response.data) {
                        setActivityData(response.data);
                    } else {
                        setActivityData(null);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch deal activity:", err);
                if (isMounted) setError("Unable to load activity data.");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchActivity();

        return () => {
            isMounted = false;
        };
    }, [id]);

    const formatDate = (dateString, format = 'medium') => {
        if (!dateString) return 'No input';
        // If it's an object, extract date value
        if (typeof dateString === 'object' && dateString !== null) {
            dateString = dateString.scheduled_date || dateString.date || dateString.toString();
        }
        try {
            const date = new Date(dateString);
            // Check if date is valid
            if (isNaN(date.getTime())) return 'Invalid date';
            if (format === 'short') {
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
            if (format === 'long') {
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            }
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch (e) {
            return 'Invalid date';
        }
    };

    // Helper function to get call score styling
    const getCallScoreStyle = (score) => {
        if (score === null || score === undefined) {
            return { 
                color: '#6b7280', 
                backgroundColor: '#f9fafb',
                borderColor: '#e5e7eb'
            };
        }
        
        const numScore = Number(score);
        if (numScore >= 7) {
            return { 
                color: '#22c55e', 
                backgroundColor: '#f0fdf4',
                borderColor: '#bbf7d0'
            };
        } else if (numScore >= 4) {
            return { 
                color: '#2196f3', 
                backgroundColor: '#eff6ff',
                borderColor: '#bfdbfe'
            };
        } else {
            return { 
                color: '#ff9800', 
                backgroundColor: '#fff7ed',
                borderColor: '#fed7aa'
            };
        }
    };

    const tabs = [
        "Deal Interaction History",
        "Sales Suggestion",
        "Customer Sentiments",
        "Customer Sentiment Analysis",
        "Alerts"
    ];

    const renderEmptyState = (message, icon = <Info size={ 24 } />) => (
        <div style={ {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            color: '#9ca3af',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px dashed #e5e7eb',
            textAlign: 'center',
            flex: 1
        } }>
            <div style={ { opacity: 0.5, marginBottom: '12px' } }>{ icon }</div>
            <div style={ { fontSize: '14px', fontWeight: 500 } }>{ message }</div>
        </div>
    );

    const renderLoadingState = () => (
        <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280', flexDirection: 'column', gap: '12px', padding: '60px' } }>
            <ActivityIcon className="spin-slow" size={ 32 } color="#e5e7eb" />
            <span style={ { fontSize: '14px' } }>Loading activity data...</span>
            <style>{ `@keyframes spin { 100% { transform: rotate(360deg); } } .spin-slow { animation: spin 3s linear infinite; }` }</style>
        </div>
    );

    const renderErrorState = () => (
        <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ef4444', padding: '60px' } }>
            <AlertTriangle size={ 20 } style={ { marginRight: '8px' } } />
            { error }
        </div>
    );

    const renderComingSoon = (featureName) => (
        <div style={ {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 20px',
            color: '#6b7280',
            background: '#f9fafb',
            borderRadius: '8px',
            textAlign: 'center',
            flex: 1
        } }>
            <div style={ {
                width: '48px', height: '48px',
                background: '#f3f4f6',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '16px'
            } }>
                <Lock size={ 20 } color="#9ca3af" />
            </div>
            <h3 style={ { fontSize: '16px', fontWeight: 600, color: '#111827', margin: '0 0 8px 0' } }>Coming Soon</h3>
            <p style={ { fontSize: '14px', maxWidth: '300px', margin: 0 } }>
                The <strong>{ featureName }</strong> module is currently under development. Stay tuned for updates!
            </p>
        </div>
    );

    const renderContent = () => {
        if (loading) return renderLoadingState();
        if (error) return renderErrorState();
        if (!activityData) return renderEmptyState("No input");

        switch (activeTab) {
            case "Deal Interaction History":
                const history = activityData.dealActivity || [];
                if (history.length === 0) return renderEmptyState("No input");

                return (
                    <div style={ { display: 'flex', flexDirection: 'column', gap: '12px' } }>
                        { history.map((item, idx) => {
                            const isEmail = item.meetingType?.toLowerCase().includes('email');
                            const icon = isEmail ? <LinkIcon size={ 18 } /> : <Video size={ 18 } />;
                            const callScoreStyle = getCallScoreStyle(item.callScore);

                            return (
                                <div key={ idx } style={ {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '16px 20px',
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                                } }>
                                    <div style={ { display: 'flex', alignItems: 'center', gap: '16px', flex: 1 } }>
                                        {/* Icon Circle */ }
                                        <div style={ {
                                            width: '44px',
                                            height: '44px',
                                            borderRadius: '50%',
                                            border: '1px solid #dcfce7',
                                            backgroundColor: '#f0fdf4',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#16a34a',
                                            flexShrink: 0
                                        } }>
                                            { icon }
                                        </div>

                                        {/* Text Content */ }
                                        <div style={ { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 } }>
                                            <div
                                                onClick={ (e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (item.meetingId) {
                                                        navigate(`/home/meeting/${item.meetingId}`);
                                                    }
                                                } }
                                                style={ {
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                    color: '#111827',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    cursor: item.meetingId ? 'pointer' : 'default'
                                                } }
                                                onMouseEnter={ (e) => { if (item.meetingId) { e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.textDecoration = 'underline'; } } }
                                                onMouseLeave={ (e) => { if (item.meetingId) { e.currentTarget.style.color = '#111827'; e.currentTarget.style.textDecoration = 'none'; } } }
                                            >
                                                { item.meetingName || "Untitled Interaction" }
                                            </div>
                                            <div style={ { fontSize: '12px', color: '#6b7280', fontWeight: 500 } }>
                                                { formatDate(item.startTime, 'long') } • { item.totalDuration?.formatted || '00:00' }
                                            </div>
                                        </div>
                                    </div>

                                    <div style={ { display: 'flex', alignItems: 'center', gap: '12px' } }>
                                        {/* Call Score Display */ }
                                        <div style={ {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '6px 12px',
                                            backgroundColor: callScoreStyle.backgroundColor,
                                            borderRadius: '20px',
                                            border: `1px solid ${callScoreStyle.borderColor}`,
                                            minWidth: '100px',
                                            justifyContent: 'center'
                                        } }>
                                            <Star size={ 14 } color={ callScoreStyle.color } />
                                            <span style={ {
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                color: '#6b7280',
                                                marginRight: '2px'
                                            } }>
                                                Call Score:
                                            </span>
                                            <span style={ {
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: callScoreStyle.color
                                            } }>
                                                { item.callScore !== null && item.callScore !== undefined 
                                                    ? Number(item.callScore).toFixed(1) 
                                                    : 'N/A' }
                                            </span>
                                        </div>

                                        {/* View Button */ }
                                        { item.meetingId && (
                                            <div
                                                onClick={ (e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (id) sessionStorage.setItem(`leadDetailTab_${id}`, 'Activity');
                                                    navigate(`/home/meeting/${item.meetingId}`);
                                                } }
                                                style={ {
                                                    display: 'flex', alignItems: 'center', gap: '4px',
                                                    fontSize: '13px', fontWeight: 600, color: '#2563eb',
                                                    textDecoration: 'none', padding: '6px 12px',
                                                    background: '#eff6ff', borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    flexShrink: 0
                                                } }
                                            >
                                                View
                                                <ExternalLink size={ 14 } />
                                            </div>
                                        ) }

                                        {/* Coaching Button */ }
                                        <div
                                            onClick={ (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const base = item.meetingId
                                                    ? `/home/coaching/${item.meetingId}`
                                                    : '/home/coaching';
                                                const path = id ? `${base}?dealId=${id}` : base;
                                                // Save active tab so LeadDetail restores it on back navigation
                                                if (id) sessionStorage.setItem(`leadDetailTab_${id}`, 'Activity');
                                                navigate(path, { state: { fromTab: 'Activity' } });
                                            } }
                                            style={ {
                                                display: 'flex', alignItems: 'center', gap: '4px',
                                                fontSize: '13px', fontWeight: 600, color: '#7c3aed',
                                                padding: '6px 12px',
                                                background: '#f5f3ff', borderRadius: '6px',
                                                cursor: 'pointer', flexShrink: 0,
                                                border: '1px solid #ede9fe'
                                            } }
                                            onMouseEnter={ (e) => e.currentTarget.style.background = '#ede9fe' }
                                            onMouseLeave={ (e) => e.currentTarget.style.background = '#f5f3ff' }
                                        >
                                            <GraduationCap size={ 14 } />
                                            Coaching
                                        </div>
                                    </div>
                                </div>
                            );
                        }) }
                    </div>
                );

            case "Sales Suggestion":
                const suggestions = activityData.suggestionsForSalesperson || [];
                if (suggestions.length === 0) return renderEmptyState("No input", <Lightbulb size={ 24 } />);

                return (
                    <div style={ { display: 'flex', flexDirection: 'column', gap: '16px' } }>
                        { suggestions.map((item, idx) => {
                            let text = "";
                            let time = null;

                            if (typeof item === 'string') {
                                text = item;
                            } else if (typeof item === 'object' && item !== null) {
                                // Handle both {suggestion: "..."} and {text: "..."} formats
                                text = item.suggestion || item.text || "No suggestion text";
                                time = item.mentioned_time;
                            } else {
                                text = "Invalid suggestion format";
                            }

                            return (
                                <div key={ idx } style={ {
                                    display: 'flex',
                                    gap: '16px',
                                    padding: '20px',
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    alignItems: 'flex-start'
                                } }>
                                    <div style={ {
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: '#fffbeb',
                                        border: '1px solid #fcd34d',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#d97706',
                                        flexShrink: 0
                                    } }>
                                        <Lightbulb size={ 20 } />
                                    </div>
                                    <div style={ { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 } }>
                                        <div style={ { fontSize: '15px', color: '#1f2937', fontWeight: 500, lineHeight: '1.5' } }>
                                            { text }
                                        </div>
                                        { time && (
                                            <div style={ { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' } }>
                                                <PlayCircle size={ 14 } />
                                                <span>Mentioned at <strong>{ time }</strong></span>
                                            </div>
                                        ) }
                                    </div>
                                </div>
                            );
                        }) }
                    </div>
                );

            case "Customer Sentiments":
                return renderComingSoon("Customer Sentiment Tracking");

            case "Customer Sentiment Analysis":
                return renderComingSoon("AI Sentiment Analysis");

            case "Alerts":
                return renderComingSoon("Smart Alerts");

            default:
                return <div>Select a tab</div>;
        }
    };

    // Safe access for top metrics
    // If loading, we show "-" or "..." but keep the cards visible
    const data = activityData || {};

    // Extract top metrics safely
    const lastMeetingTime = loading ? "..." : (data.lastMeeting?.startTime
        ? formatDate(data.lastMeeting.startTime, 'long')
        : 'No input');

    // Handle nextStepDate - it might be a string, object, or null
    let nextStepDateValue = data.nextStepDate;
    if (typeof nextStepDateValue === 'object' && nextStepDateValue !== null) {
        // If it's an object, try to extract scheduled_date or text fields
        nextStepDateValue = nextStepDateValue.scheduled_date || nextStepDateValue.date || null;
    }
    const nextStepTime = loading ? "..." : (nextStepDateValue
        ? formatDate(nextStepDateValue, 'long')
        : 'No input');

    const scoreValue = data.engagementScore;
    const isScoreNull = scoreValue === null || scoreValue === undefined;
    const score = loading ? "..." : (isScoreNull ? "No input" : scoreValue);
    const touchpoints = loading ? "-" : (data.numberOfMeetings || 0);

    const scoreColor = (loading || isScoreNull) ? '#6b7280' : (Number(scoreValue) >= 7 ? '#16a34a' : Number(scoreValue) >= 4 ? '#eab308' : '#ef4444');
    const scoreLabel = (loading || isScoreNull) ? '' : (Number(scoreValue) >= 7 ? 'High' : Number(scoreValue) >= 4 ? 'Medium' : 'Low');

    return (
        <div style={ { height: '100%', display: 'flex', flexDirection: 'column', gap: '16px', overflow: 'hidden' } }>

            {/* 4 Static Metric Divs - Always Visible */ }
            <div style={ { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', flexShrink: 0 } }>

                {/* Last Activity Date */ }
                <div style={ { background: '#fff', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' } }>
                    <div style={ { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#6b7280' } }>
                        <Clock size={ 16 } />
                        <div style={ { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' } }>Last Activity</div>
                    </div>
                    <div style={ { fontSize: '16px', fontWeight: 700, color: '#111827' } }>{ lastMeetingTime }</div>
                </div>

                {/* Next Activity Date */ }
                <div style={ { background: '#fff', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' } }>
                    <div style={ { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#6b7280' } }>
                        <Calendar size={ 16 } />
                        <div style={ { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' } }>Next Follow-up</div>
                    </div>
                    <div style={ { fontSize: '16px', fontWeight: 700, color: '#2563eb' } }>{ nextStepTime }</div>
                </div>

                {/* Customer Engagement Score */ }
                <div style={ { background: '#fff', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' } }>
                    <div style={ { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#6b7280' } }>
                        <BarChart2 size={ 16 } />
                        <div style={ { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' } }>Engagement Score</div>
                    </div>
                    <div style={ { display: 'flex', alignItems: 'end', gap: '8px' } }>
                        <div style={ {
                            fontSize: (isScoreNull && !loading) ? '14px' : '20px',
                            fontWeight: 700,
                            color: (isScoreNull && !loading) ? '#9ca3af' : '#111827',
                            lineHeight: 1,
                            fontStyle: (isScoreNull && !loading) ? 'italic' : 'normal'
                        } }>{ score }</div>
                        { scoreLabel && (
                            <div style={ {
                                fontSize: '12px',
                                color: scoreColor,
                                fontWeight: 600,
                                marginBottom: '2px'
                            } }>
                                { scoreLabel }
                            </div>
                        ) }
                    </div>
                </div>

                {/* Total Touchpoints */ }
                <div style={ { background: '#fff', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' } }>
                    <div style={ { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#6b7280' } }>
                        <MessageSquare size={ 16 } />
                        <div style={ { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' } }>Touchpoints</div>
                    </div>
                    <div style={ { fontSize: '20px', fontWeight: 700, color: '#111827', lineHeight: 1 } }>{ touchpoints }</div>
                </div>

            </div>

            {/* Main Content Card with Tabs - Always Visible */ }
            <div style={ { background: '#fff', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }>

                {/* Navigation Tabs */ }
                <div style={ { display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 16px', background: '#f9fafb', overflowX: 'auto', scrollbarWidth: 'none' } }>
                    { tabs.map(tab => (
                        <div
                            key={ tab }
                            onClick={ () => setActiveTab(tab) }
                            style={ {
                                padding: '16px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                color: activeTab === tab ? '#2563eb' : '#6b7280',
                                borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            } }
                        >
                            { tab }
                        </div>
                    )) }
                </div>

                {/* Scrollable Content - Renders Loader/Error/Data */ }
                <div style={ { flex: 1, padding: '16px', paddingBottom: '60px', overflowY: 'auto', minHeight: 0 } }>
                    { renderContent() }
                </div>

            </div>

        </div>
    );
}
